import { Container, Text } from 'pixi.js';
import type { Disposable } from '../../core/Event';
import type { BoardModel } from '../../model/board/BoardModel';
import { BalanceDisplay } from '../../widgets/BalanceDisplay';
import { PrimaryButton } from '../../widgets/PrimaryButton';
import type { SceneComponent, SceneLayoutContext } from '../SceneComponent';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

export interface BoardHudCallbacks {
  onRoll: () => void;
}

/**
 * Board-game HUD: balance counter (top), ROLL button (bottom), status
 * label below the button. Subscribes to the model so balance + status
 * stay current without scene-side wiring.
 */
export class BoardHudComponent extends Container implements SceneComponent {
  private readonly balance: BalanceDisplay;
  private readonly rollButton: PrimaryButton;
  private readonly statusLabel: Text;
  private readonly subscriptions: Disposable[] = [];

  constructor(
    private readonly model: BoardModel,
    private readonly callbacks: BoardHudCallbacks,
  ) {
    super();
    this.balance = new BalanceDisplay(this.model.balance);
    this.rollButton = new PrimaryButton({
      label: 'ROLL',
      width: 200,
      fillActive: 0xff6bd6,
      fillHover: 0x6b3d77,
      textColor: 0xffffff,
      onTap: () => this.callbacks.onRoll(),
    });
    this.statusLabel = new Text({
      text: 'tap ROLL or press space to advance',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 13,
        fontWeight: '500',
        fill: 0xffffff,
        align: 'center',
      },
    });
    this.statusLabel.anchor.set(0.5);
    this.statusLabel.alpha = 0.7;

    this.addChild(this.balance, this.rollButton, this.statusLabel);

    this.subscriptions.push(
      this.model.events.on('balance-changed', () => this.balance.setAmount(this.model.balance)),
      this.model.events.on('roll-resolved', (event) => {
        this.statusLabel.text = `🎲 rolled ${event.value}`;
      }),
      this.model.events.on('tile-activated', (event) => {
        const reward = event.tile.reward > 0 ? ` → +${event.tile.reward}` : '';
        this.statusLabel.text = `${event.tile.label}${reward}`;
      }),
      this.model.events.on('goal-reached', () => {
        this.statusLabel.text = `🏆 SOLVED — back to the start for another lap!`;
      }),
    );
  }

  layout(ctx: SceneLayoutContext): void {
    const { centerX, machineTop, spinButtonY } = ctx;
    this.balance.x = centerX;
    this.balance.y = Math.max(16, machineTop - 96);
    this.rollButton.x = centerX;
    this.rollButton.y = spinButtonY;
    this.statusLabel.x = centerX;
    this.statusLabel.y = spinButtonY + 56;
  }

  destroy(): void {
    for (const sub of this.subscriptions) sub.dispose();
    this.subscriptions.length = 0;
    this.balance.destroyDisplay();
    super.destroy({ children: true });
  }

  setRolling(rolling: boolean): void {
    this.rollButton.setLabel(rolling ? '…' : 'ROLL');
    this.rollButton.setSuppressHover(rolling);
  }

  setStatusText(text: string): void {
    this.statusLabel.text = text;
  }
}
